/**
 * Evidence Service
 * Handles evidence upload, review, and management API calls
 */
import axios from 'axios';
import BaseApiService from '../base/BaseApiService';
import config from '@config';
import authService from '../authServiceFactory';
import type { Evidence, EvidenceForReview, AssessorEvidenceStatistics } from '@app-types';

class EvidenceService extends BaseApiService {
  /**
   * Upload evidence with files
   * Special case: Uses axios directly with FormData
   */
  async uploadEvidence(evidenceData: {
    assessment_item_id: number;
    evidence_type: 'document' | 'screenshot' | 'policy' | 'procedure' | 'other';
    evidence_name: string;
    evidence_description: string;
    notes?: string;
    files?: File[];
  }): Promise<{
    message: string;
    evidence: Evidence;
  }> {
    // Create FormData for file upload
    const formData = new FormData();

    // Add text fields
    formData.append('assessment_item_id', evidenceData.assessment_item_id.toString());
    formData.append('evidence_type', evidenceData.evidence_type);
    formData.append('evidence_name', evidenceData.evidence_name);
    formData.append('evidence_description', evidenceData.evidence_description);
    if (evidenceData.notes) {
      formData.append('notes', evidenceData.notes);
    }

    // Add files
    if (evidenceData.files && evidenceData.files.length > 0) {
      evidenceData.files.forEach((file) => {
        formData.append('files', file);
      });
    }

    // Get Keycloak token
    const service = await authService;
    const token = await service.getAccessToken();

    // Send FormData with proper headers
    const response = await axios.post<{
      message: string;
      evidence: Evidence;
    }>(`${config.apiBaseUrl}/evidence`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  }

  /**
   * Get evidence by ID with files from Azure Blob Storage
   */
  async getEvidenceById(evidenceId: number): Promise<Evidence> {
    const response = await this.get<{ evidence: Evidence }>(`/evidence/${evidenceId}`);
    return response.evidence;
  }

  /**
   * Get evidence for a specific assessment item
   */
  async getEvidenceForItem(assessmentItemId: number): Promise<{
    assessment_item_id: number;
    evidence_count: number;
    evidence: Evidence[];
  }> {
    return this.get<{
      assessment_item_id: number;
      evidence_count: number;
      evidence: Evidence[];
    }>(`/evidence/assessment-item/${assessmentItemId}`);
  }

  /**
   * Review evidence (approve or reject)
   */
  async reviewEvidence(
    evidenceId: number,
    status: 'approved' | 'rejected',
    reviewNotes: string,
    remediationDate?: string
  ): Promise<{
    message: string;
    evidence: Evidence;
  }> {
    const requestData: {
      status: 'approved' | 'rejected';
      review_notes: string;
      remediation_date?: string;
    } = {
      status,
      review_notes: reviewNotes
    };

    // Add remediation_date if provided (for gap evidence acceptance)
    if (remediationDate) {
      requestData.remediation_date = remediationDate;
    }

    return this.put<{
      message: string;
      evidence: Evidence;
    }>(`/evidence/${evidenceId}/review`, requestData);
  }

  /**
   * Get evidence for contract manager
   */
  async getContractManagerEvidence(): Promise<{
    contract_manager_id: number;
    evidence_count: number;
    evidence: Evidence[];
  }> {
    return this.get<{
      contract_manager_id: number;
      evidence_count: number;
      evidence: Evidence[];
    }>('/contract-manager/evidence');
  }

  /**
   * Get pending evidence for assessor
   */
  async getPendingEvidenceForAssessor(): Promise<{
    assessor_id: number;
    status_filter: string;
    evidence_count: number;
    pending_evidence_count: number;
    statistics: AssessorEvidenceStatistics;
    evidence: EvidenceForReview[];
  }> {
    return this.get<{
      assessor_id: number;
      status_filter: string;
      evidence_count: number;
      pending_evidence_count: number;
      statistics: AssessorEvidenceStatistics;
      evidence: EvidenceForReview[];
    }>('/evidence/pending/assessor?status=all');
  }

  /**
   * Update evidence metadata
   */
  async updateEvidence(
    evidenceId: number,
    updateData: {
      evidence_type?: string;
      evidence_name?: string;
      evidence_description?: string;
      notes?: string;
    }
  ): Promise<{
    message: string;
    evidence: Evidence;
  }> {
    return this.put<{
      message: string;
      evidence: Evidence;
    }>(`/evidence/${evidenceId}`, updateData);
  }

  /**
   * Submit Draft Evidence (draft -> pending). Contract Manager / Contract Owner only.
   */
  async submitEvidence(evidenceId: number): Promise<{
    message: string;
    evidence: Evidence;
  }> {
    return this.post<{
      message: string;
      evidence: Evidence;
    }>(`/evidence/${evidenceId}/submit`);
  }

  /**
   * Add files to existing evidence
   * Special case: Uses axios directly with FormData (same as uploadEvidence)
   */
  async addEvidenceFiles(
    evidenceId: number,
    files: File[]
  ): Promise<{
    message: string;
    evidence: Evidence;
  }> {
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    const service = await authService;
    const token = await service.getAccessToken();

    const response = await axios.post<{
      message: string;
      evidence: Evidence;
    }>(`${config.apiBaseUrl}/evidence/${evidenceId}/files`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        Authorization: `Bearer ${token}`
      }
    });

    return response.data;
  }

  /**
   * Remove one file from evidence
   */
  async deleteEvidenceFile(
    evidenceId: number,
    fileId: string
  ): Promise<{
    message: string;
    evidence: Evidence;
  }> {
    return this.delete<{
      message: string;
      evidence: Evidence;
    }>(`/evidence/${evidenceId}/files/${encodeURIComponent(fileId)}`);
  }

  /**
   * Delete evidence
   */
  async deleteEvidence(evidenceId: number): Promise<{
    message: string;
  }> {
    return this.delete<{
      message: string;
    }>(`/evidence/${evidenceId}`);
  }
}

export const evidenceService = new EvidenceService();
export default evidenceService;
